import {
  DownloadOutlined,
  FileTextOutlined,
  LoadingOutlined,
  SaveOutlined,
} from '@ant-design/icons'
import { pdf } from '@react-pdf/renderer'
import { Alert, Button, message, Progress, Space, Typography } from 'antd'
import { format } from 'date-fns'
import { useRolodexEdit } from 'features/fde/rolodexAPI'
import { PDFDocument } from 'features/reporting/pdfGeneration/PDFDocument'
import { PDFGenerationSection } from 'features/reporting/types'
import { useContextBridge } from 'its-fine'
import {
  useVisualReportOps,
  VisualReportContext,
} from 'pages/reporting/reports/externalVisual/VisualReportProvider'
import React, { useCallback, useState } from 'react'

const { Title, Text } = Typography

const RenderReact: React.FC = () => {
  const contextValue = useVisualReportOps()
  const {
    pdfState,
    publishLatestDraftPDF,
    handleDownloadLatestDraft,
    handleDownloadPublishedPdf,
    isSubmitting,
    report,
    metadataEntryId,
    selectedVisualInspection,
  } = contextValue

  // State for PDF generation
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)
  const [currentStep, setCurrentStep] = useState<string>('')

  // API hooks
  const { rolodexUploadFile } = useRolodexEdit()

  const Bridge = useContextBridge()

  // Generate PDF
  const generatePDF = useCallback(async () => {
    setProgress(0)
    setCurrentStep('Preparing PDF document...')
    setProgress(20)

    const pdfInstance = (
      <VisualReportContext.Provider value={contextValue}>
        <Bridge>
          <PDFDocument
            report={report}
            onPageRender={({ id }) => {
              setProgress((prev) => Math.min(prev + 8, 90))
              setCurrentStep(`Generating ${id} page...`)
            }}
          />
        </Bridge>
      </VisualReportContext.Provider>
    )

    setCurrentStep('Finalizing PDF...')
    const blob = await pdf(pdfInstance).toBlob()
    setProgress(100)
    return blob
  }, [contextValue, report, Bridge])

  // Generate and save PDF draft
  const handleGeneratePDF = useCallback(async () => {
    try {
      setIsGenerating(true)
      const pdfBlob = await generatePDF()

      const timestamp = format(new Date(), 'yyyyMMdd-HHmmss')
      if (selectedVisualInspection) {
        const path = `reportPDFDrafts/${selectedVisualInspection.id}-visual-report-${metadataEntryId}-draft-${timestamp}.pdf`
        await rolodexUploadFile(metadataEntryId, pdfBlob, path)
      }

      message.success('PDF generated successfully')
      // Reload the page or update state to show the new draft
      window.location.reload()
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      message.error('Failed to generate PDF')
    } finally {
      setIsGenerating(false)
      setProgress(0)
      setCurrentStep('')
    }
  }, [
    generatePDF,
    selectedVisualInspection,
    metadataEntryId,
    rolodexUploadFile,
  ])

  return (
    <div style={{ padding: '24px', maxWidth: '800px' }}>
      <Title level={2}>Generate PDF</Title>
      <Text>
        Generate and download the final external visual inspection report PDF.
        You can create a draft version for review or publish the final version.
      </Text>

      <div style={{ marginTop: '32px' }}>
        <Space orientation='vertical' size='large' style={{ width: '100%' }}>
          <div>
            <Title level={4}>Draft PDF</Title>
            <Text type='secondary'>
              Generate a draft PDF for review and internal use.
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Space
                orientation='vertical'
                size='small'
                style={{ width: '100%' }}>
                {isGenerating ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <Space
                      orientation='vertical'
                      align='center'
                      style={{ width: '100%' }}>
                      <LoadingOutlined style={{ fontSize: 24 }} />
                      <Progress percent={progress} status='active' />
                      <Text>{currentStep}</Text>
                    </Space>
                  </div>
                ) : (
                  <Space wrap>
                    <Button
                      type='primary'
                      icon={<SaveOutlined />}
                      onClick={handleGeneratePDF}
                      loading={isGenerating}
                      disabled={isGenerating}>
                      Generate PDF
                    </Button>
                    {pdfState.latestDraftTimestamp && (
                      <>
                        <Button
                          icon={<FileTextOutlined />}
                          onClick={handleDownloadLatestDraft}
                          loading={isSubmitting}>
                          Download Latest Draft
                        </Button>
                        <Text type='secondary'>
                          Last generated:{' '}
                          {new Date(
                            pdfState.latestDraftTimestamp
                          ).toLocaleString()}
                        </Text>
                      </>
                    )}
                  </Space>
                )}
              </Space>
            </div>
          </div>

          <div>
            <Title level={4}>Published PDF</Title>
            <Text type='secondary'>
              Publish and download the final official external visual inspection
              report PDF.
            </Text>
            <div style={{ marginTop: '16px' }}>
              <Space orientation='vertical' size='small'>
                {!pdfState.hasPublishedPdf ? (
                  <Button
                    type='primary'
                    danger
                    icon={<DownloadOutlined />}
                    onClick={publishLatestDraftPDF}
                    loading={isSubmitting}>
                    Publish Final PDF
                  </Button>
                ) : (
                  <Space>
                    <Button
                      type='primary'
                      icon={<DownloadOutlined />}
                      onClick={handleDownloadPublishedPdf}>
                      Download Published PDF
                    </Button>
                    <Button
                      type='default'
                      onClick={publishLatestDraftPDF}
                      loading={isSubmitting}>
                      Update Published PDF
                    </Button>
                  </Space>
                )}
                {pdfState.hasPublishedPdf && (
                  <Alert
                    title='PDF Published'
                    description='The official external visual inspection report PDF has been published and is available for download.'
                    type='success'
                    showIcon
                  />
                )}
              </Space>
            </div>
          </div>
        </Space>
      </div>
    </div>
  )
}

export const PDFGeneration: PDFGenerationSection = {
  id: 'pdfGeneration',
  title: 'Generate PDF',
  description: 'Generate and download final report',
  RenderReact,
  required: false,
}
